<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { useBankTransfer } from '@/composables/useBankTransfer';
import { placeholderBackgroundColor } from '@/helpers/colors';
import { formatIBAN, getInitials } from '@/helpers/formatters';
import {
  type BankTransferRecipient,
  BankTransferRecipientType
} from '@/references/axios/bankTransfer/types';
import { useI18n } from '@/references/i18n';
import { RouteNames } from '@/router';

import { InnerLayoutButtonType } from '@/components/layouts/InnerLayout.types';
import InnerLayout from '@/components/layouts/InnerLayout.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiColoredCircle from '@/components/ui/UiColoredCircle.vue';
import type { UiListPairGroup } from '@/components/ui/UiListPair.types';
import UiListPair from '@/components/ui/UiListPair.vue';
import UiSkeleton from '@/components/ui/UiSkeleton.vue';
import UiTitle from '@/components/ui/UiTitle.vue';

interface Props {
  id: string;
}

const props = defineProps<Props>();

const { t } = useI18n();
const { replace, push } = useRouter();
const { loadRecipients, recipients, isLoading } = useBankTransfer();

const currentItem = computed((): BankTransferRecipient | undefined =>
  recipients.value.find((item) => item.id === Number(props.id))
);

const name = computed((): string => {
  if (currentItem.value?.nickname) {
    return currentItem.value.nickname;
  }

  if (currentItem.value?.type === BankTransferRecipientType.Business) {
    return currentItem.value.firstName.toUpperCase();
  }

  return `${currentItem.value?.firstName || ''} ${currentItem.value?.lastName || ''}`.toUpperCase();
});

const formattedIBAN = computed(() => formatIBAN(currentItem.value?.iban ?? ''));

const details = computed((): UiListPairGroup[] => [
  {
    items: [
      {
        label: t('detailsBeneficiary.firstName'),
        value: currentItem.value?.firstName.toUpperCase(),
        hidden: currentItem.value?.type === BankTransferRecipientType.Business
      },
      {
        label: t('detailsBeneficiary.lastName'),
        value: currentItem.value?.lastName.toUpperCase(),
        hidden: currentItem.value?.type === BankTransferRecipientType.Business
      },
      {
        label: t('detailsBeneficiary.companyName'),
        value: currentItem.value?.firstName.toUpperCase(),
        hidden: currentItem.value?.type === BankTransferRecipientType.Individual
      }
    ].filter((item) => !item.hidden && !!item.value)
  },
  {
    items: [
      {
        label: t('detailsBeneficiary.iban'),
        value: formattedIBAN.value
      },
      {
        label: t('detailsBeneficiary.bic'),
        value: currentItem.value?.bic
      },
      {
        label: t('detailsBeneficiary.bank'),
        value: currentItem.value?.bankName
      }
    ].filter((item) => !!item.value)
  }
]);

function goBack(): void {
  replace({ name: RouteNames.Home });
}

function onClick(): void {
  if (!currentItem.value) {
    return;
  }

  push({ name: RouteNames.OTC, params: { id: props.id } });
}

(async () => {
  if (!currentItem.value) {
    await loadRecipients();
  }

  if (!currentItem.value) {
    replace({ name: RouteNames.Home });
  }
})();
</script>

<template>
  <section class="bank-transfer-beneficiary-view">
    <InnerLayout :button-type="InnerLayoutButtonType.Close" @back="goBack">
      <div class="bank-transfer-beneficiary-view__inner">
        <div v-if="isLoading" class="bank-transfer-beneficiary-view__skeleton-group">
          <UiSkeleton
            class="bank-transfer-beneficiary-view__skeleton bank-transfer-beneficiary-view__skeleton_avatar"
            border-radius="50%"
            width="80px"
            height="80px"
          />
          <UiSkeleton
            class="bank-transfer-beneficiary-view__skeleton bank-transfer-beneficiary-view__skeleton_title"
            width="160px"
            height="24px"
          />
        </div>
        <template v-else-if="currentItem">
          <UiColoredCircle
            class="bank-transfer-beneficiary-view__avatar"
            :text="getInitials(name)"
            :color="placeholderBackgroundColor(name)"
            size="lg"
          />
          <UiTitle class="bank-transfer-beneficiary-view__title" :level="1" align="center">
            {{ name }}
          </UiTitle>
          <UiListPair
            class="bank-transfer-beneficiary-view__details"
            :details="details"
            mod="table"
          />
          <UiButton class="bank-transfer-beneficiary-view__button" mod="secondary" @click="onClick">
            {{ t('buttons.otc') }}
          </UiButton>
        </template>
      </div>
    </InnerLayout>
  </section>
</template>

<style scoped>
.bank-transfer-beneficiary-view {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.bank-transfer-beneficiary-view__inner {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 24px 0 0;
}

.bank-transfer-beneficiary-view__skeleton-group {
  display: flex;
  flex-direction: column;
}

.bank-transfer-beneficiary-view__skeleton_avatar {
  margin: 0 auto 24px;
}

.bank-transfer-beneficiary-view__skeleton_title {
  max-width: 100% !important;
  margin: 0 auto 48px;
}

.bank-transfer-beneficiary-view__avatar {
  margin: 0 auto 16px;
}

.bank-transfer-beneficiary-view__title {
  margin: 0 auto 40px;
  word-break: break-word;
}

.bank-transfer-beneficiary-view__details:not(:last-child) {
  margin: 0 0 40px;
}

.bank-transfer-beneficiary-view__button {
  margin: auto 0 0;
}
</style>
